import React, { useState, useMemo, useCallback } from 'react';
import { adocSectionIdDataAttribute } from '../../const';
import TocMenuItem from './TocMenuItem';

type Props = {
  elements: Element[];
};

const buildHierarchy = (elements: Element[]) => {
  const result: any[] = [];
  let currentH2: any = null;
  let currentH3: any = null;
  elements.forEach((element) => {
    if (element.nodeName === 'H2') {
      currentH2 = { element, subItems: [] };
      currentH3 = null;
      result.push(currentH2);
    } else if (element.nodeName === 'H3' && currentH2) {
      currentH3 = { element, subItems: [] };
      currentH2.subItems.push(currentH3);
    } else if (element.nodeName === 'H4' && currentH3) {
      currentH3.subItems.push({ element, subItems: [] });
    }
  });
  return result;
};

const TocMenu = ({ elements }: Props) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());
  const filteredElements = useMemo(
    () => elements.filter((element) => element.getAttribute(adocSectionIdDataAttribute) !== null),
    [elements]
  );
  const tree = useMemo(() => buildHierarchy(filteredElements), [filteredElements]);

  const handleSetActive = useCallback(
    (id: string, parentId?: string) => {
      setOpenSections((prev) => {
        const next = new Set<string>();
        next.add(id);

        // Add current to list so it will expand accordion correctly.
        if (parentId) {
          next.add(parentId);
        }

        // Find and add all parents.
        tree.forEach((h2Item) => {
          const h2Id = h2Item.element.getAttribute(adocSectionIdDataAttribute)!;

          h2Item.subItems?.forEach((h3Item: any) => {
            const h3Id = h3Item.element.getAttribute(adocSectionIdDataAttribute)!;

            if (h3Id === id) {
              next.add(h2Id);
            }

            h3Item.subItems?.forEach((h4Item: any) => {
              if (h4Item.element.getAttribute(adocSectionIdDataAttribute) === id) {
                next.add(h3Id);
                next.add(h2Id);
              }
            });
          });
        });

        if (prev.size === next.size && [...prev].every((item) => next.has(item))) {
          return prev;
        }

        return next;
      });
    },
    [tree]
  );

  const toggleSection = useCallback((id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <div
      className="fixed top-16 bottom-0 overflow-hidden hide-scrollbar overflow-x-hidden 
                hidden lg:flex lg:flex-col bg-white dark:bg-very-dark-grey"
    >
      {tree.length > 0 && (
        <>
          <div className="flex-none pt-4">
            <h2 className="font-semibold text-base dark:text-bright-white py-2">ON THIS PAGE</h2>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain hide-scrollbar overflow-x-hidden pb-24">
            {tree.map((item) => (
              <TocMenuItem
                key={item.element.getAttribute(adocSectionIdDataAttribute)}
                element={item.element}
                subItems={item.subItems}
                openSections={openSections}
                onToggle={toggleSection}
                onSetActive={handleSetActive}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};
export default TocMenu;
